import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional, REQUEST } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ConfigGlobalModel } from 'app/models/config-global-model';
import { LandingPageConfig } from 'app/pages/landing-page/landing-page.models';
import { AppService } from '../app/app.service';
import { SUPPORTED_LANGUAGES } from '../language/languages';

@Injectable({ providedIn: 'root' })
export class SeoService {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Optional() @Inject(REQUEST) private request: Request | null,
    private title: Title,
    private meta: Meta,
    private appService: AppService,
  ) {}

  updateRoute(path: string): void {
    const route = this.request ? new URL(this.request.url).pathname : path;
    const pathname = route.split(/[?#]/, 1)[0].replace(/\/$/, '') || '/';
    const origin = this.origin();
    const canonical = `${origin}${pathname}`;
    this.setLink('canonical', canonical);
    this.meta.updateTag({ property: 'og:url', content: canonical });

    const landing = SUPPORTED_LANGUAGES.some(lang => pathname === `/${lang}`);
    this.document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(link => link.remove());
    if (landing) {
      for (const lang of SUPPORTED_LANGUAGES) {
        const link = this.document.createElement('link');
        link.rel = 'alternate';
        link.hreflang = lang;
        link.href = `${origin}/${lang}`;
        this.document.head.appendChild(link);
      }
      const fallback = this.document.createElement('link');
      fallback.rel = 'alternate';
      fallback.hreflang = 'x-default';
      fallback.href = `${origin}/es`;
      this.document.head.appendChild(fallback);
    } else {
      this.document.getElementById('landing-local-business')?.remove();
    }
  }

  updateLanding(config: LandingPageConfig, globalConfig: ConfigGlobalModel): void {
    const brand = globalConfig.name?.trim();
    const title = brand && !config.title.toLowerCase().includes(brand.toLowerCase())
      ? `${config.title} | ${brand}` : config.title;
    const description = this.appService.getTraduccion('landing.seo.description', {
      brand: brand || config.title,
      title: config.title,
    });
    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    const image = config.heroImage?.imageLarge || config.heroImage?.imageMedium
      || config.heroImage?.imageSmall || 'assets/landing/hero-malaga.jpg';
    this.meta.updateTag({ property: 'og:image', content: new URL(image, `${this.origin()}/`).href });

    const businesses = (globalConfig.locations || [])
      .filter(location => location.name && location.address)
      .map(location => ({
        '@type': 'LocalBusiness',
        name: location.name,
        address: {
          '@type': 'PostalAddress',
          streetAddress: location.address,
          ...(location.city ? { addressLocality: location.city } : {}),
        },
        ...(globalConfig.phone ? { telephone: globalConfig.phone } : {}),
        ...(location.latitude != null && location.longitude != null
          && Number.isFinite(Number(location.latitude)) && Number.isFinite(Number(location.longitude))
          ? { geo: { '@type': 'GeoCoordinates', latitude: Number(location.latitude), longitude: Number(location.longitude) } }
          : {}),
      }));
    this.document.getElementById('landing-local-business')?.remove();
    if (businesses.length) {
      const script = this.document.createElement('script');
      script.id = 'landing-local-business';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': businesses }).replace(/</g, '\\u003c');
      this.document.head.appendChild(script);
    }
  }

  private origin(): string {
    const url = this.request ? new URL(this.request.url) : new URL(this.document.location.href);
    if (url.hostname.startsWith('www.')) url.hostname = url.hostname.slice(4);
    return url.origin;
  }

  private setLink(rel: string, href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!link) {
      link = this.document.createElement('link');
      link.rel = rel;
      this.document.head.appendChild(link);
    }
    link.href = href;
  }
}
